import React, { useState, useEffect } from 'react';
import { createApiUrl } from '../utils/apiConfig';
import axios from 'axios';
import UnifiedStudentProgress from './UnifiedStudentProgress';
import styles from './ProgressDashboard.module.css';

const ProgressDashboard = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    yearGrade: '',
    search: ''
  });
  const [sortBy, setSortBy] = useState('name');
  const [analytics, setAnalytics] = useState(null);
  const [yearGroups, setYearGroups] = useState([]);

  // Load data on component mount
  useEffect(() => {
    fetchStudentsProgress();
    fetchYearGroups();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Only refetch when non-search filters change
  useEffect(() => {
    fetchStudentsProgress(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when these filters change
  }, [filters.status, filters.yearGrade, sortBy]);

  const fetchStudentsProgress = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      // Always clear error at the start
      setError(null);
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.yearGrade) params.append('yearGrade', filters.yearGrade);
      // Remove search from API call - handle it on frontend
      if (sortBy) params.append('sortBy', sortBy);

      const response = await axios.get(createApiUrl(`/api/students/progress?${params.toString()}`), {
        withCredentials: true
      });
      
      // If API returns success: true, NEVER show error (even if students array is empty)
      if (response.data && response.data.success === true) {
        setStudents(response.data.data?.students || []);
        setError(null); // Explicitly clear error
        return; // Exit early to avoid any other error handling
      }
      
      // If success is false, check the message
      if (response.data && response.data.success === false) {
        const message = response.data.message || '';
        // Don't show error for "not found" or "no students" - treat as empty result
        if (message.toLowerCase().includes('not found') || 
            message.toLowerCase().includes('no students') ||
            message.toLowerCase().includes('empty')) {
          setStudents([]);
          setError(null);
        } else {
          setError(message || 'Failed to load students progress');
        }
      } else {
        // Unexpected response - assume success with empty array
        setStudents([]);
        setError(null);
      }
    } catch (err) {
      // Only show error for actual server errors (5xx)
      if (err.response && err.response.status >= 500) {
        console.error('Server error fetching students progress:', err);
        setError('Failed to load students progress');
      } else {
        // For all other errors (4xx, network, etc.), don't show error
        // Treat as empty result - this handles empty database gracefully
        console.error('Error fetching students progress:', err);
        setStudents([]);
        setError(null);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(createApiUrl('/api/progress/analytics'), {
        withCredentials: true
      });
      
      if (response.data && response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (err) {
      // Handle 404 gracefully - analytics endpoint might not be available
      if (err.response && err.response.status === 404) {
        console.warn('Analytics endpoint not available');
        // Don't show error, just don't display analytics
        setAnalytics(null);
      } else {
        console.error('Error fetching analytics:', err);
      }
      // Don't set analytics on error - keep it hidden
    }
  };

  const fetchYearGroups = async () => {
    try {
      const response = await axios.get(createApiUrl('/api/classes'), {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Extract year groups from the classes data
        const yearGroups = response.data.data.map(cls => cls.grade).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        setYearGroups(yearGroups);
      }
    } catch (err) {
      console.error('Error fetching year groups:', err);
    }
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowStudentModal(true);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'success';
    if (percentage >= 70) return 'primary';
    if (percentage >= 50) return 'warning';
    return 'danger';
  };

  const getGradeColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 80) return 'text-primary';
    if (score >= 70) return 'text-warning';
    return 'text-danger';
  };

  const getStatusBadge = (student) => {
    const { progress } = student;
    if (progress.progressPercentage === 100) {
      return { variant: 'success', text: 'Completed' };
    }
    if (progress.progressPercentage >= 70) {
      return { variant: 'primary', text: 'On Track' };
    }
    if (progress.progressPercentage >= 50) {
      return { variant: 'warning', text: 'Needs Attention' };
    }
    return { variant: 'danger', text: 'Behind' };
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>⏳</div>
        <p className={styles.loadingText}>Loading students progress...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorAlert}>
        {error}
      </div>
    );
  }

  return (
    <div className={styles.progressDashboard}>
      <div className={styles.dashboardHeader}>
        <h3 className={styles.dashboardTitle}>Student Progress Dashboard</h3>
        <div className={styles.dashboardActions}>
          <button className={styles.analyticsButton} onClick={() => {
            if (analytics) {
              setAnalytics(null);
            } else {
              fetchAnalytics();
            }
          }}>
            📊 {analytics ? 'Hide Analytics' : 'View Analytics'}
          </button>
        </div>
      </div>

      {/* Analytics Summary */}
      {analytics !== null && (
        <div className={styles.analyticsContainer}>
          <div className={styles.analyticsCard}>
            <div className={styles.analyticsCardBody}>
              <h4 className={styles.analyticsNumber}>{students.length}</h4>
              <p className={styles.analyticsLabel}>Total Students</p>
            </div>
          </div>
          <div className={styles.analyticsCard}>
            <div className={styles.analyticsCardBody}>
              <h4 className={styles.analyticsNumber}>
                {students.filter(s => s.progress.progressPercentage === 100).length}
              </h4>
              <p className={styles.analyticsLabel}>Completed All</p>
            </div>
          </div>
          <div className={styles.analyticsCard}>
            <div className={styles.analyticsCardBody}>
              <h4 className={styles.analyticsNumber}>
                {students.filter(s => s.progress.progressPercentage < 70).length}
              </h4>
              <p className={styles.analyticsLabel}>Need Attention</p>
            </div>
          </div>
          <div className={styles.analyticsCard}>
            <div className={styles.analyticsCardBody}>
              <h4 className={styles.analyticsNumber}>
                {Math.round(students.reduce((sum, s) => sum + s.progress.averageScore, 0) / students.length) || 0}%
              </h4>
              <p className={styles.analyticsLabel}>Average Score</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filtersCard}>
        <div className={styles.filtersBody}>
          <div className={styles.filtersRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Search Students</label>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Search by name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Status Filter</label>
              <select
                className={styles.filterSelect}
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="all">All Students</option>
                <option value="high-achievers">High Achievers (85%+)</option>
                <option value="on-track">On Track (70-84%)</option>
                <option value="needs-attention">Needs Attention (&lt;70%)</option>
                <option value="completed">Completed All</option>
                <option value="in-progress">In Progress</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Year Group</label>
              <select
                className={styles.filterSelect}
                value={filters.yearGrade}
                onChange={(e) => handleFilterChange('yearGrade', e.target.value)}
              >
                <option value="">All Year Groups</option>
                {yearGroups.map(yearGroup => (
                  <option key={yearGroup} value={yearGroup}>Year {yearGroup}</option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Sort By</label>
              <select
                className={styles.filterSelect}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name">Name</option>
                <option value="progress">Progress %</option>
                <option value="score">Average Score</option>
                <option value="recent">Recently Active</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <button 
                className={styles.clearButton}
                onClick={() => setFilters({ status: 'all', yearGrade: '', search: '' })}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className={styles.studentsCard}>
        <div className={styles.studentsBody}>
          {students.length === 0 ? (
            <div className={styles.emptyState}>
              <h5 className={styles.emptyStateTitle}>No students found</h5>
              <p className={styles.emptyStateText}>Try adjusting your filters or add students to your year group.</p>
            </div>
          ) : (
            <div className={styles.studentsTable}>
              <table className={styles.studentsTable}>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Progress</th>
                    <th>Activities</th>
                    <th>Average Score</th>
                    <th>Status</th>
                    <th>Last Activity</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students
                    .filter(student => 
                      !filters.search || 
                      `${student.firstName} ${student.lastName}`.toLowerCase().includes(filters.search.toLowerCase())
                    )
                    .map((student) => {
                      const status = getStatusBadge(student);
                      return (
                        <tr key={student._id}>
                          <td>
                            <div className={styles.studentInfo}>
                              <h6>
                                {student.firstName} {student.lastName}
                              </h6>
                              <small className={styles.studentYearGroup}>Year {student.yearGrade || 'N/A'}</small>
                            </div>
                          </td>
                          <td>
                            <div className={styles.progressInfo}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                                <span className={styles.progressText}>
                                  {student.progress.progressPercentage}%
                                </span>
                                <span className={styles.statusBadge}>
                                  {student.progress.completedActivities}/{student.progress.totalActivities}
                                </span>
                              </div>
                              <div className={styles.progressBarContainer}>
                                <div 
                                  className={`${styles.progressBarFill} bg-${getProgressColor(student.progress.progressPercentage)}`}
                                  style={{ width: `${student.progress.progressPercentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className={styles.activitiesBreakdown}>
                              <div className={styles.activityItem}>
                                <span className={styles.activityIcon}>🚀</span>
                                <span className={styles.activityCount}>
                                  {student.progress.missions.completed}/{student.progress.missions.total}
                                </span>
                                <small className={styles.activityLabel}>Missions</small>
                              </div>
                              <div className={styles.activityItem}>
                                <span className={styles.activityIcon}>❓</span>
                                <span className={styles.activityCount}>
                                  {student.progress.quizzes.completed}/{student.progress.quizzes.total}
                                </span>
                                <small className={styles.activityLabel}>Quizzes</small>
                              </div>
                              <div className={styles.activityItem}>
                                <span className={styles.activityIcon}>📚</span>
                                <span className={styles.activityCount}>
                                  {student.progress.lessons.completed}/{student.progress.lessons.total}
                                </span>
                                <small className={styles.activityLabel}>Lessons</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`${styles.scoreDisplay} ${getGradeColor(student.progress.averageScore)}`}>
                              {student.progress.averageScore}%
                            </span>
                          </td>
                          <td>
                            <span className={`${styles.statusBadge} bg-${status.variant}`}>
                              {status.text}
                            </span>
                          </td>
                          <td>
                            <small>
                              {student.updatedAt ? new Date(student.updatedAt).toLocaleDateString() : 'Never'}
                            </small>
                          </td>
                          <td>
                            <button
                              className={styles.actionButton}
                              onClick={() => handleViewStudent(student)}
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Student Detail Modal */}
      {showStudentModal && selectedStudent && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'var(--lightPurple)',
            borderRadius: '10px',
            padding: '2rem',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <UnifiedStudentProgress
              studentId={selectedStudent._id}
              onClose={() => setShowStudentModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressDashboard;
